// Vendor-quote extraction pipeline used by MultiDocPanel (Section 8).
// Mirrors EstimatingAssistant.jsx's Add Quote flow so quotes turned over here
// land in the same Quotes/QuoteLineItems tables an estimator would use.

import { callClaude } from "../../utils/claudeClient";
import { sqlRead, sqlWrite, clean as cleanStr } from "../../utils/sqlClient";

const QUOTE_PDF_HEADER_SYSTEM = `You are extracting data from a vendor quote PDF for the CleanSpace estimating database.
Return ONLY a valid JSON object. No markdown, no preamble, no explanation — just the raw JSON.
Keys required:
vendor_name, vendor_id, manufacturer_name (the brand/manufacturer of the equipment — may differ from vendor), contact_name, contact_email, contact_phone,
project_name, client, category, subcategory, equipment_type, description,
qty (number or null), unit_price (number or null), total_price (number or null),
freight (number or null), lead_time_weeks (number or null), quote_valid_days (number or null),
exclusions, tariff_exposure, quote_number, notes, total_line_item_count (integer — count of ALL distinct line items in the quote),
attributes (array of objects with keys: name, value, unit, source — max 10 items).
Known vendor IDs: V001=Tristate HVAC, V002=ISAT, V003=General Aire, V004=AJ Manufacturing, V005=Cannaris, V006=Princeton Cryo, V007=Pro Comp. If unknown vendor, leave vendor_id blank.
Valid categories: HVAC — Custom / Semi-Custom AHU, HVAC — Packaged DX RTU, HVAC — Fan Powered Plenum / LFM, Humidifier, HEPA Filtration System, Seismic, Pump Skid, Controls / BAS, Panels — Wall & Ceiling, Doors & Hardware, Flooring, Pass-Throughs, Lighting, Cold Room / Freezer, Dust Collector, Lab Fixtures / Utilities, LN2 / Process Gases, Fire Protection, High Speed Roll-Up Doors, Emergency Generator, Cleanroom Certification, Air Balancing, Hydronic Balancing, Wall Protection, Other.
For subcategory: provide the specific product type within the category (examples: Bollards, Handrails, Corner Guards, Bumper Rails, Wall Guards, Door Frames — leave blank if not applicable).
CRITICAL: All numeric values must be plain numbers without commas (e.g. 250000 not 250,000). Keep description under 500 characters. Never use single quotes or apostrophes in any text field — write dimensions as 2x4 not 2' x 4', write contractions without apostrophes.`;

const QUOTE_PDF_LINEITEMS_SYSTEM = `You are extracting line items from a vendor quote PDF for the CleanSpace estimating database.
Return ONLY a valid JSON object with a single key "line_items" containing an array. No markdown, no preamble.
Each line item object must have: line_number (int), description (string max 150 chars), qty (number or null), unit (EA/LS/LF/SF/HR), unit_price (number or null), extended_price (number or null), category (string), capacity (array).
For capacity: extract measurable performance/size specs. Examples: AHU → [{type:"Airflow",value:10000,unit:"CFM"},{type:"Cooling",value:30,unit:"Tons"}]. Leave as [] if none.
CRITICAL: All numeric values must be plain numbers without commas. Never use single quotes or apostrophes in any text field — write dimensions as 2x4 not 2' x 4'. Return complete valid JSON.`;

function cleanNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return isNaN(n) ? null : n;
}

async function claudeExtractQuote(systemPrompt, userContent, maxTokens = 3000) {
  const text = await callClaude(systemPrompt, userContent, maxTokens);
  const first = text.indexOf("{");
  const last  = text.lastIndexOf("}");
  if (first === -1 || last === -1) throw new Error("No JSON in response");
  return JSON.parse(text.slice(first, last + 1));
}

export async function checkQuoteDuplicate(vendorName, quoteDate) {
  if (!vendorName || !quoteDate) return null;
  try {
    const rows = await sqlRead(`EXEC sp_DuplicateCheckQuote @vendor_name='${cleanStr(vendorName)}', @quote_date='${cleanStr(quoteDate)}'`);
    return rows && rows.length > 0 ? rows[0].QuoteID : null;
  } catch { return null; }
}

export async function extractQuoteFromPdf(file) {
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });

  const header = await claudeExtractQuote(QUOTE_PDF_HEADER_SYSTEM, [
    { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
    { type: "text", text: "Extract the quote header data and count all line items." },
  ], 2000);

  const totalItems = header.total_line_item_count || 0;
  const batches = Math.max(1, Math.ceil(totalItems / 10));
  let allLineItems = [];
  for (let b = 0; b < batches; b++) {
    const startItem = b * 10 + 1;
    const endItem   = Math.min((b + 1) * 10, totalItems || 10);
    try {
      const batchResult = await claudeExtractQuote(QUOTE_PDF_LINEITEMS_SYSTEM, [
        { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
        { type: "text", text: totalItems > 10
          ? `Extract ONLY line items ${startItem} through ${endItem}. Return as JSON with key "line_items".`
          : `Extract ALL line items. Return as JSON with key "line_items".` },
      ], 3000);
      allLineItems = allLineItems.concat(batchResult.line_items || []);
    } catch (err) { console.warn(`[Quote Extract] Batch ${b + 1} failed:`, err.message); }
  }
  return { ...header, line_items: allLineItems };
}

export async function writeQuoteToSQL(extracted) {
  const cleanLineItems = items => (items || []).map(li => ({
    ...li,
    description:    cleanStr(li.description),
    unit:           cleanStr(li.unit),
    category:       cleanStr(li.category),
    qty:            cleanNum(li.qty),
    unit_price:     cleanNum(li.unit_price),
    extended_price: cleanNum(li.extended_price),
    capacity: (li.capacity || []).map(c => ({
      ...c,
      type:  cleanStr(c.type),
      unit:  cleanStr(c.unit),
      value: cleanNum(c.value),
    })),
  }));

  try {
    const data = await sqlWrite({
      vendor_name:       cleanStr(extracted.vendor_name     || ""),
      manufacturer_name: cleanStr(extracted.manufacturer_name || ""),
      trade:             cleanStr(extracted.trade           || extracted.category || ""),
      subcategory:       cleanStr(extracted.subcategory     || ""),
      quote_date:        extracted.quote_date      || "",
      total_amount:      cleanNum(extracted.total_amount || extracted.total_price) || 0,
      scope_area_sf:     cleanNum(extracted.scope_area_sf) || null,
      scope:             cleanStr(extracted.scope           || extracted.description || ""),
      exclusions:        cleanStr(extracted.exclusions      || ""),
      lead_time_weeks:   cleanNum(extracted.lead_time_weeks),
      valid_until:       extracted.valid_until     || null,
      source_file:       cleanStr(extracted.source_file     || ""),
      source_file_url:   extracted.source_file_url || null,
      notes:             cleanStr(extracted.notes           || ""),
      hubspot_deal_id:   extracted.hubspot_deal_id || null,
      loaded_by_name:    extracted.loaded_by_name  || null,
      loaded_by_email:   extracted.loaded_by_email || null,
      contact_name:      cleanStr(extracted.contact_name  || "") || null,
      contact_email:     cleanStr(extracted.contact_email || "") || null,
      contact_phone:     cleanStr(extracted.contact_phone || "") || null,
      line_items:        cleanLineItems(extracted.line_items),
      attributes: (extracted.attributes || []).map(a => ({
        name:   cleanStr(a.name  || ""),
        value:  cleanStr(a.value || ""),
        unit:   cleanStr(a.unit  || ""),
        source: cleanStr(a.source || "AI_Extracted"),
      })),
    });
    return data.success !== false ? data : false;
  } catch {
    return false;
  }
}
